import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  InputAdornment,
  FormHelperText,
  Popover,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
} from "@mui/material";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRange } from "react-date-range";
import { es } from "date-fns/locale";
import {
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarMonthIcon,
} from "@mui/icons-material";
import { FormData, FormErrors } from "./types";
import { Estacionamiento } from "../../types/Estacionamiento";
import { useEstacionamientos } from "../../hooks/useEstacionamientos";
import { useZonas } from "../calendar/hooks/useZonas";
import { useZoneFilter } from "../calendar/contexts/ZoneFilterContext";
import { useFilteredDepartments } from "../calendar/hooks/useFilteredDepartments";
import { isInternalDepartment } from "../calendar/utils/internalDepartments";
import { Z } from "../../theme/zIndex";

interface TabEstacionamientoProps {
  formData: FormData;
  errors: FormErrors;
  soloEstacionamiento?: boolean;
  handleInputChange: (
    field: keyof FormData,
    value: string | number | boolean | string[] | undefined
  ) => void;
  handleDateRangeClick: (event: React.MouseEvent<HTMLElement>) => void;
  openDateRange: boolean;
  dateRangeAnchorEl: HTMLElement | null;
  handleDateRangeClose: () => void;
  handleDateRangeChange: (
    rangesByKey: import("react-date-range").RangeKeyDict
  ) => void;
  dateRange: {
    startDate: Date;
    endDate: Date;
    key: string;
  };
  calcularNoches: () => number;
}

const TabEstacionamiento: React.FC<TabEstacionamientoProps> = ({
  formData,
  errors,
  soloEstacionamiento = false,
  handleInputChange, // Recibir prop
  handleDateRangeClick,
  openDateRange,
  dateRangeAnchorEl,
  handleDateRangeClose,
  handleDateRangeChange,
  dateRange,
  calcularNoches,
}) => {
  const { data: estacionamientos, isLoading: loadingEstacionamientos } =
    useEstacionamientos();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Obtener departamentos para el dropdown
  const { zonas, loading: loadingZonas, error: errorZonas } = useZonas();
  const { zoneFilter } = useZoneFilter();
  const departamentos = useFilteredDepartments({
    zonas,
    zoneFilter,
    loadingZonas,
    errorZonas,
  }).filter((depto) => !isInternalDepartment(depto));

  // La función handleInputChange se recibe ahora por props, por lo que se elimina de aquí.

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value);
  };

  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 1.75 },
        pr: { xs: 1.75, sm: 2.25 },
        pb: 1.5,
      }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            <CarIcon
              sx={{ mr: 0.75, verticalAlign: "middle", fontSize: "1.2rem" }}
            />
            Información de Estacionamiento
          </Typography>
        </Box>

        <Box>
          <TextField
            fullWidth
            size="small"
            label="Fechas de estacionamiento"
            value={`${dateRange.startDate.toLocaleDateString("es-AR", {
              day: "numeric",
              month: "short",
            })} - ${dateRange.endDate.toLocaleDateString("es-AR", {
              day: "numeric",
              month: "short",
            })}`}
            onClick={handleDateRangeClick}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthIcon
                    sx={{ color: "primary.main", fontSize: "1.1rem" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          {/* Popover se mantiene igual */}
          <Popover
            open={openDateRange}
            anchorEl={dateRangeAnchorEl}
            onClose={handleDateRangeClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              zIndex: Z.datepicker,
              "& .MuiPopover-paper": {
                maxWidth: isMobile ? "350px" : isTablet ? "650px" : "700px",
                width: "100%",
                minWidth: isMobile ? "320px" : isTablet ? "600px" : "650px",
              },
            }}
          >
            <Box
              sx={{
                p: isMobile ? 1 : 2,
                "& .rdrDayPassive": {
                  opacity: 0.4,
                  pointerEvents: "none",
                },
                "& .rdrDayPassive .rdrDayNumber span": {
                  color: theme.palette.text.disabled,
                },
              }}
            >
              <DateRange
                ranges={[dateRange]}
                onChange={handleDateRangeChange}
                months={isMobile ? 1 : 2}
                direction={isMobile ? "vertical" : "horizontal"}
                locale={es}
                showDateDisplay={false}
                rangeColors={[theme.palette.primary.main]}
                monthDisplayFormat="MMM yyyy"
                showPreview={false}
                minDate={new Date(2024, 0, 1)}
                sx={{
                  "& .rdrMonth": {
                    width: isMobile ? "280px" : "320px",
                  },
                  "& .rdrCalendarWrapper": {
                    fontSize: isMobile ? "12px" : "14px",
                  },
                  "& .rdrMonthAndYearWrapper": {
                    fontSize: isMobile ? "14px" : "16px",
                  },
                  "& .rdrDays .rdrDayPassive": {
                    opacity: 0.4,
                    pointerEvents: "none",
                  },
                  "& .rdrDayPassive .rdrDayNumber span": {
                    color: theme.palette.text.disabled,
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button variant="contained" color="primary" onClick={handleDateRangeClose} size="small">
                  Aplicar
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>

        {soloEstacionamiento && (
          <Stack spacing={1.25}>
            <Divider />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              <PersonIcon
                sx={{ mr: 0.75, verticalAlign: "middle", fontSize: "1.2rem" }}
              />
              Información del Cliente
            </Typography>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nombre"
                  name="nombre"
                  value={formData.nombre || ""}
                  onChange={(e) => handleInputChange("nombre", e.target.value)}
                  error={!!errors.nombre}
                  helperText={errors.nombre}
                  inputProps={{ "aria-label": "Nombre" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Apellido"
                  name="apellido"
                  value={formData.apellido || ""}
                  onChange={(e) => handleInputChange("apellido", e.target.value)}
                  error={!!errors.apellido}
                  helperText={errors.apellido}
                  inputProps={{ "aria-label": "Apellido" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="DNI"
                  name="dni"
                  value={formData.dni || ""}
                  onChange={(e) => handleInputChange("dni", e.target.value)}
                  error={!!errors.dni}
                  helperText={errors.dni}
                  inputProps={{ "aria-label": "DNI" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}

        <Grid container spacing={1.25}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" error={!!errors.estacionamiento}>
              <InputLabel>Estacionamiento</InputLabel>
              <Select
                value={formData.estacionamiento || ""}
                onChange={(e) => handleInputChange("estacionamiento", e.target.value)}
                label="Estacionamiento"
                startAdornment={
                  <InputAdornment position="start">
                    <CarIcon sx={{ color: "primary.main" }} />
                  </InputAdornment>
                }
                MenuProps={{ sx: { zIndex: Z.popover } }}
              >
                <MenuItem value="">Sin estacionamiento</MenuItem>
                {loadingEstacionamientos ? (
                  <MenuItem disabled>Cargando estacionamientos...</MenuItem>
                ) : (
                  (estacionamientos as Estacionamiento[] | undefined)?.map((est) => (
                    <MenuItem key={est.id} value={est.nombre}>
                      {est.nombre}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.estacionamiento && (
                <FormHelperText>{errors.estacionamiento}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Departamento Asociado</InputLabel>
              <Select
                value={formData.departamento_asociado || ""}
                onChange={(e) => handleInputChange("departamento_asociado", e.target.value)}
                label="Departamento Asociado"
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "primary.main" }} />
                  </InputAdornment>
                }
                MenuProps={{ sx: { zIndex: Z.popover } }}
              >
                <MenuItem value="">Sin departamento asociado</MenuItem>
                {loadingZonas ? (
                  <MenuItem disabled>Cargando departamentos...</MenuItem>
                ) : (
                  departamentos.map((depto: string) => (
                    <MenuItem key={depto} value={depto}>
                      {depto}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {!soloEstacionamiento && formData.departamento_asociado && (
          <Stack spacing={1.25}>
            <Divider />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              <PersonIcon
                sx={{ mr: 0.75, verticalAlign: "middle", fontSize: "1.2rem" }}
              />
              Información del Cliente (sincronizado)
            </Typography>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nombre"
                  name="nombre"
                  value={formData.nombre || ""}
                  inputProps={{ "aria-label": "Nombre sincronizado" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Apellido"
                  name="apellido"
                  value={formData.apellido || ""}
                  inputProps={{ "aria-label": "Apellido sincronizado" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="DNI"
                  name="dni"
                  value={formData.dni || ""}
                  inputProps={{ "aria-label": "DNI sincronizado" }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}

        <Divider sx={{ my: 0.25 }} />

        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            <MoneyIcon
              sx={{ mr: 0.75, verticalAlign: "middle", fontSize: "1.2rem" }}
            />
            Pago de Estacionamiento
          </Typography>
          <Grid container spacing={1.25}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Precio/noche"
                type="number"
                value={formData.precio_estacionamiento_por_dia || 0}
                onChange={(e) =>
                  handleInputChange(
                    "precio_estacionamiento_por_dia",
                    parseFloat(e.target.value) || 0
                  )
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Noches"
                value={calcularNoches()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthIcon
                        sx={{ color: "primary.main", fontSize: "1.1rem" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Valor Total"
                value={formatCurrency(formData.valor_total_estacionamiento || 0)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Pago Recibido"
                type="number"
                value={formData.pago_estacionamiento || 0}
                onChange={(e) =>
                  handleInputChange(
                    "pago_estacionamiento",
                    parseFloat(e.target.value) || 0
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Cobrado por</InputLabel>
                <Select
                  value={formData.cobrado_por_estacionamiento || ""}
                  onChange={(e) =>
                    handleInputChange("cobrado_por_estacionamiento", e.target.value)
                  }
                  label="Cobrado por"
                  MenuProps={{ sx: { zIndex: Z.popover } }}
                >
                  {["Mariela", "Andrea", "Gabriel"].map((nombre) => (
                    <MenuItem key={nombre} value={nombre}>
                      {nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notas"
                multiline
                rows={isMobile ? 2 : 3}
                value={formData.notas_estacionamiento || ""}
                onChange={(e) => handleInputChange("notas_estacionamiento", e.target.value)}
                placeholder="Información adicional sobre el estacionamiento..."
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Box>
  );
};

export default TabEstacionamiento;
